import { useSessionContext } from "ui/context/SessionContext";
import { usePublishContext } from "ui/context/PublishContext";
import { useNotifications } from "ui/context/NotificationsContext";

import { Fragment, SubtitleExportValue, PublisherParams } from "core/models";
import createNewFormat from "utils/createNewFormat";
import { publishFragment } from "core/services/site/fragment";
import fragmentPublish from "utils/fragmentPublish";

interface HookReturn {
    share(
        platform: "Facebook" | "Twitter" | "YouTube",
        fragment: Fragment,
        publisherKey: string,
        subtitleExport: SubtitleExportValue,
        publisherParams: PublisherParams,
    ): Promise<[boolean, Fragment | undefined, string]>;
    publish(
        fragment: Fragment,
        selectedOutput: string,
        selectedModule: string,
    ): Promise<void>;
}

const usePublish = (): HookReturn => {
    const sessionContext = useSessionContext();
    const publishContext = usePublishContext();
    const { addNotification, updateNotification } = useNotifications();

    const ensureVariantExists = async (
        fragment: Fragment,
        formatKey: string,
    ): Promise<boolean> => {
        if (
            !fragment.variants.some(
                (variant) => variant.formatKey === formatKey,
            )
        ) {
            try {
                if (!sessionContext.session?.token) {
                    throw Error("Missing session token");
                }
                await createNewFormat(
                    formatKey,
                    sessionContext.session.token,
                    fragment.id,
                );
                // TODO notify user when creating new variant
                return true;
            } catch (err) {
                return false;
            }
        } else {
            return true;
        }
    };
    const getSubtitleFormatKey = async (
        fragment: Fragment,
    ): Promise<string> => {
        try {
            const subtitleFormat = publishContext.publishFormats.find(
                (format) => format.tags?.some((tag) => tag === "Subtitles"),
            );
            if (!subtitleFormat) {
                throw Error("No Subtitle publish format available ");
            }
            const subtitleVariantExists = await ensureVariantExists(
                fragment,
                subtitleFormat.formatkey,
            );
            if (!subtitleVariantExists) {
                throw Error("Create subtitle publish format failed");
            }
            return subtitleFormat.formatkey;
        } catch (err) {
            throw Error((err as Error).message);
        }
    };

    const share = async (
        platform: "Facebook" | "Twitter" | "YouTube",
        fragment: Fragment,
        publisherKey: string,
        subtitleExport: SubtitleExportValue,
        publisherParams: PublisherParams,
    ): Promise<[boolean, Fragment | undefined, string]> => {
        const publishNotification = addNotification(
            "normal",
            `Starting Share to ${platform}`,
            "Share initiated",
            0,
        );
        try {
            if (!sessionContext.session?.token) {
                throw Error("session token missing");
            }
            let formatKey = "mp4";
            if (subtitleExport === "burn") {
                formatKey = await getSubtitleFormatKey(fragment);
            }
            const publishResult = await publishFragment(
                sessionContext.session.token,
                fragment.id,
                publisherKey,
                formatKey,
                (subtitleExport === "export" &&
                    fragment.getSubtitlesLanguage()) ||
                    undefined,
                publisherParams,
            );
            if (publishResult.error || !publishResult.value) {
                throw new Error(publishResult.error);
            }
            return [true, publishResult.value, publishNotification.id];
        } catch (err) {
            updateNotification(
                publishNotification.id,
                "error",
                `Share to ${platform} failed`,
                (err as Error).message,
                0,
            );
            return [false, undefined, publishNotification.id];
        }
    };

    const publish = async (
        fragment: Fragment,
        selectedOutput: string,
        selectedModule: string,
    ): Promise<void> => {
        const publishNotification = addNotification(
            "normal",
            `Publishing ${fragment.title}`,
            "Hold on a minute",
            0,
        );

        try {
            const variantExists = await ensureVariantExists(
                fragment,
                selectedOutput,
            );
            if (!sessionContext.session?.token) {
                throw Error("Missing session token");
            }
            if (!variantExists) {
                throw Error("Unable to create new variant");
            }
            updateNotification(
                publishNotification.id,
                "normal",
                "Publish in progess",
                `For ${fragment.title}`,
                0,
            );
            await fragmentPublish(
                selectedOutput,
                selectedModule,
                sessionContext.session.token,
                fragment.id,
            );
            updateNotification(
                publishNotification.id,
                "success",
                `Publish Finished`,
                `For ${fragment.title}`,
                5000,
            );
        } catch (err) {
            updateNotification(
                publishNotification.id,
                "error",
                "error",
                (err as Error).message,
                5000,
            );
        }
    };

    return {
        share,
        publish,
    };
};
export default usePublish;
